/**
 * Firebase web config, read from the environment.
 *
 * This used to hardcode the production projectId, which meant the web client could
 * only ever reach production Firestore, Auth and Remote Config — and Remote Config
 * is where the dual-write and read-cutover flags live, so there was nowhere to
 * rehearse a cutover.
 *
 * There is deliberately NO fallback. An absent value throws at module load rather
 * than defaulting to the production project, because a staging build silently
 * talking to production is the exact failure the staging environment exists to
 * prevent. These seven values are not secrets — they ship inside the web bundle —
 * so they live in .env / .env.production / .env.staging, and
 * apps/atlas/scripts/submit-web.sh refuses a prod deploy when any is empty.
 *
 * Native builds do not use this file: @react-native-firebase reads
 * GoogleService-Info.plist / google-services.json, selected per variant in
 * app.config.js.
 *
 * IMPORTANT: each `process.env.EXPO_PUBLIC_FIREBASE_*` read below MUST be a
 * static literal member expression (`process.env.THE_NAME`), never a computed
 * one (`process.env[someVariable]`). `babel-preset-expo`'s production
 * env-inlining pass only rewrites reads whose key is a string literal it can
 * see at build time — a computed key is left as a real `process.env` lookup,
 * which is `undefined` in the shipped web bundle (no runtime process.env
 * polyfill exists outside dev mode). That reintroduces exactly the failure
 * this file exists to prevent, except worse: it throws on every production
 * page load. See firebaseConfig.inlining.test.js, which transforms this file
 * with babel-preset-expo in production mode and fails if any read stops being
 * inlined.
 */

// Static reads only — see the note above. Do not refactor this into a loop
// over a name->envVar map; that reintroduces the computed-key bug.
const RAW = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.EXPO_PUBLIC_FIREBASE_DATABASE_URL,
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
};

// Map fields to the env var names used to report which ones are missing.
const ENV_VAR_NAMES = {
  apiKey: 'EXPO_PUBLIC_FIREBASE_API_KEY',
  authDomain: 'EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN',
  databaseURL: 'EXPO_PUBLIC_FIREBASE_DATABASE_URL',
  projectId: 'EXPO_PUBLIC_FIREBASE_PROJECT_ID',
  storageBucket: 'EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'EXPO_PUBLIC_FIREBASE_APP_ID',
};

const config = {};
const missing = [];

// This loop only ever touches the already-resolved RAW values (plain data by
// this point) — it never itself reads process.env, so it is safe to iterate.
for (const [field, value] of Object.entries(RAW)) {
  if (typeof value !== 'string' || value === '') {
    missing.push(ENV_VAR_NAMES[field]);
  } else {
    config[field] = value;
  }
}

if (missing.length > 0) {
  throw new Error(
    `Firebase config is incomplete — missing or empty: ${missing.join(', ')}. ` +
      'Set these in apps/atlas/.env (dev), .env.production (prod web), or .env.staging ' +
      '(staging). There is no fallback on purpose: defaulting to the production ' +
      'project would let a staging build write to real data.',
  );
}

export default config;
